import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  StyleSheet,
  View,
  SafeAreaView,
  Platform,
  useWindowDimensions,
  FlatList,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { Card } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { useTranslation } from "react-i18next";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import {
  changeDateFromDDMMYYYYtoYYYYMMDD,
  changeDateFromYYYYMMDDToDDMMYYYY,
  formatDateForUI,
} from "../../../constants/FormatDate";
import {
  useHRAdminEmployees,
} from "../../../hooks/useHRAdminEmployees";
import { useTableHelper } from "../../../hooks/useTableHelper";
import Swal from "sweetalert2";
import { showMessage } from "react-native-flash-message";

const NumberInput = ({ index, item, itemKey, handleEditPress }) => {
  const [number, setNumber] = useState(item[itemKey]);
  useEffect(() => {
    setNumber(item[itemKey]); // Update state when item changes
  }, [item[itemKey]]);

  return (
    <CustomInput
      value={number}
      setValue={(e) => {
        let onlyNum = e.replace(/[^0-9.]/g, ""); // allows numbers and dots
        let dotIndex = onlyNum.indexOf(".");
        if (dotIndex !== -1)
          onlyNum =
            onlyNum.slice(0, dotIndex + 1) +
            onlyNum.slice(dotIndex + 1).replace(/\./g, "");
        setNumber(onlyNum);
        handleEditPress(index, item, itemKey, onlyNum);
      }}
      maxLen={50}
      type="MAININPUT"
      keyboardType="numeric"
    />
  );
};

const AcademicQualificationsListView = React.memo(
  ({
    item,
    index,
    handleEditPress,
    styles,
    academicQualHelper,
    t,
    handleDelete
  }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [fieldName, setFieldName] = useState("")
    const [showCalendarDos, setShowCalendarDos] = useState(false);
    const [showCalendarDoa, setShowCalendarDoa] = useState(false);
    const [institutionEditMode, setInstitutionEditMode] = useState(false);
    const [selectedData, setSelectedData] = useState({
      startDate: item.dos || "",
      endDate: item.doq || "",
    });

    const dateHelper = useCallback(() => {
      let dateArr = [];
      let tmpTdate = "";
      let tmpFdate = "";
      if (
        selectedData?.startDate &&
        selectedData?.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpFdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.startDate);
      }
      if (
        selectedData?.endDate &&
        selectedData?.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        tmpTdate = changeDateFromYYYYMMDDToDDMMYYYY(selectedData?.endDate);
      }
      dateArr.push(tmpFdate);
      dateArr.push(tmpTdate);
      return dateArr;
    }, [selectedData]);

    const onShowCalendarPressDos = () => {
      setFieldName('dos')
      setShowCalendarDos(!showCalendarDos);
      setShowCalendarDoa(false);
    };

    const onShowCalendarPressDoa = () => {
      setFieldName('doq')
      setShowCalendarDoa(!showCalendarDoa);
      setShowCalendarDos(false);
    };

    const handleDateChange = (newDates) => {
      setSelectedData(newDates);
      handleEditPress(index, item, fieldName, newDates.startDate);
    };

    const toggleExpand = () => {
      setIsExpanded(!isExpanded);
    };

    const toggleInstitutionInputBox = () => {
      handleEditPress(index, item, "institution", "")
      setInstitutionEditMode(!institutionEditMode)
    }

    return (
      <View>
        <SafeAreaView>
          <SafeAreaView style={styles.row}>
            <View style={[styles.item, { flexDirection: 'row', alignItems: 'center' }]}>
              <View style={{ flex: 1 }}>
                {institutionEditMode ? (
                  <CustomInput
                    value={item.institution}
                    setValue={(text) => handleEditPress(index, item, "institution", text)}
                    editable={true}
                    maxLen={50}
                    type="MAININPUT"
                  />
                ) : (
                  <CustomDropDown
                    defValue={item.institution}
                    options={academicQualHelper.Institution}
                    value={item.institution}
                    setValue={(e) => handleEditPress(index, item, "institution", e)}
                    type="FULL"
                  />
                )}
              </View>
              <TouchableOpacity
                onPress={() => toggleInstitutionInputBox()}
                style={{ paddingTop: 15 }}
              >
                <Ionicons name="text-outline" size={24} color="#000" />
              </TouchableOpacity>
            </View>
            <CustomDropDown
              defValue={item.qualCode}
              options={academicQualHelper.QualTitle}
              value={item.qualCode}
              setValue={(e) =>
                handleEditPress(index, item, "qualCode", e)
              }
              type="FULL"
            />
            <CustomInput
              value={item.course}
              setValue={(text) => {
                handleEditPress(index, item, "course", text);
              }}
              editable={true}
              maxLen={50}
              type="MAININPUT"
            />
            <CustomDropDown
              defValue={item.result}
              options={academicQualHelper.QualResult}
              value={item.result}
              setValue={(e) =>
                handleEditPress(index, item, "result", e)
              }
              type="FULL"
            />
            <CustomDropDown
              defValue={item.status}
              options={academicQualHelper.QualStatus}
              value={item.status}
              setValue={(e) =>
                handleEditPress(index, item, "status", e)
              }
              type="FULL"
            />
            <SafeAreaView>
              <CustomButton
                iconName="trash-outline"
                onPress={() => handleDelete(item, index, 'rowGuid')}
                show="HOME_BELL"
                type="HOME_BELL"
                textTop="2"
                sz={30}
              />
              <Ionicons
                name={
                  isExpanded
                    ? "chevron-down-outline"
                    : "chevron-forward-outline"
                }
                style={styles.icons}
                onPress={toggleExpand}
              />
            </SafeAreaView>
          </SafeAreaView>
          {isExpanded && (
            <>
              <SafeAreaView style={styles.row2}>
                <View style={[styles.item]}>
                  <SafeAreaView style={[styles.row, { marginLeft: 10 }]}>
                    <SafeAreaView>
                      <CustomLabel value={t("common:dos")} type="SUBTITLE" />
                      <CustomInput
                        value={formatDateForUI(item.dos)}
                        setValue={() => { }}
                        maxLen={50}
                        editable={false}
                        type="MAININPUT"
                      />
                    </SafeAreaView>
                    <Ionicons
                      name={showCalendarDos ? "calendar" : "calendar-outline"}
                      style={styles.icons}
                      onPress={onShowCalendarPressDos}
                    />
                  </SafeAreaView>
                </View>
                <View style={[styles.item]}>
                  <SafeAreaView style={[styles.row, { marginLeft: 10 }]}>
                    <SafeAreaView>
                      <CustomLabel value={t("common:doa")} type="SUBTITLE" />
                      <CustomInput
                        value={formatDateForUI(item.doq)}
                        setValue={() => { }}
                        maxLen={50}
                        editable={false}
                        type="MAININPUT"
                      />
                    </SafeAreaView>
                    <Ionicons
                      name={showCalendarDoa ? "calendar" : "calendar-outline"}
                      style={styles.icons}
                      onPress={onShowCalendarPressDoa}
                    />
                  </SafeAreaView>
                </View>
                <View style={[styles.item]}>
                  <CustomLabel value={t("common:grade")} type="SUBTITLE" />
                  <NumberInput
                    index={index}
                    item={item}
                    itemKey="grade"
                    handleEditPress={handleEditPress}
                  />
                </View>
                <View style={[styles.item]}>
                  <CustomLabel value={t("common:nooy")} type="SUBTITLE" />
                  <NumberInput
                    index={index}
                    item={item}
                    itemKey="years"
                    handleEditPress={handleEditPress}
                  />
                </View>
                {/* <View style={[styles.item]}>
                  <CustomLabel value={t("common:type")} type="SUBTITLE" />
                  <CustomDropDown
                    defValue={item.type}
                    options={academicQualHelper.QualType}
                    value={item.type}
                    setValue={(e) =>
                      handleEditPress(index, item, "type", e)
                    }
                    type="ARRAYDROPDOWN"
                  />
                </View> */}
              </SafeAreaView>
              <SafeAreaView style={styles.row2}>
                <View style={[styles.item]}>
                  <CustomLabel value={t("common:Comments")} type="SUBTITLE" />
                  <CustomInput
                    defaultValue={item.comments}
                    setValue={(e) => handleEditPress(index, item, 'comments', e)}
                    editable={true}
                    maxLen={200}
                    multiline={true}
                    numberOfLines={5}
                    type="MAININPUT"
                  />
                </View>
              </SafeAreaView>
            </>
          )}
        </SafeAreaView>
        {
          (showCalendarDoa || showCalendarDos) && (
            <SafeAreaView style={styles.secondLine}>
              <View
                style={{
                  height:
                    Platform.OS === "web"
                      ? 285
                      : height < 670
                        ? (height * 40) / 100
                        : (height * 30) / 100,
                  width: Platform.OS === "web" ? 485 : (width * 90) / 100,
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 10,
                  borderWidth: 0,
                  borderColor: "#e8e8e8",
                }}
              >
                <CustomCalendar
                  calendarType={"RangePicker"}
                  setRange={handleDateChange}
                  range={selectedData}
                  allowSingleDate={true}
                  startingYear={1950}
                />
              </View>
            </SafeAreaView>
          )
        }
      </View >
    );
  }
);

const HRAdminAcademicQualifications = (props) => {
  const { width } = useWindowDimensions();
  const { colors } = useTheme();
  const styles = makeStyles(colors, width);
  const common = createCommonStyles(colors);
  const [changes, setChanges] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  // * Will not ivestigate duplicates for this due to time. Re-enable and check salary schemes to see how to implement. 
  // const [duplicates, setDuplicates] = useState([]);
  const [academicQualHelper, setAcademicQualHelper] = useState({});
  const [academicQual, setAcademicQual] = useState([]);
  const { t } = useTranslation();
  const ref = useRef(null);
  const { getAcademicQualHelper, getAcademicQual, saveAcademicQual, deleteAcademicQual, isLoading } = useHRAdminEmployees();
  const { rowAdd, rowEdit, rowDelete } = useTableHelper();

  const showWarningPopup = async () => {
    return Swal.fire({
      title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:changeSchemeMessage"
      )}</text>`,
      icon: "warning",
      showCancelButton: true,
      reverseButtons: true,
      cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:cancel"
      )}</text>`,
      cancelButtonColor: "#d3d3d3",
      confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
        "common:confirmMessage"
      )}</text>`,
      confirmButtonColor: "#A0A2FF",
    });
  };

  useEffect(() => {
    loadData();
  }, [props.selectedEmployee, props.selectedCompany]);

  const loadData = () => {
    getAcademicQualHelper().then((resp) => {
      setAcademicQualHelper(resp);
      getAcademicQual(props.selectedEmployee.employeeID).then((resp) => {
        setAcademicQual(resp);
        setOriginalData(resp)
      }).catch((error) => console.error(error))
    }).catch((error) => console.error(error))
  }

  const handleEditPress = useCallback(
    async (index, item, key, value) => {
      // Check if the value has changed
      if (item[key] === value) return;
      // Store the original value for potential rollback
      // Use rowEdit to apply the change
      rowEdit(
        index,
        item,
        key,
        value,
        changes,
        setChanges,
        academicQual,
        setAcademicQual
      );
    },
    [showWarningPopup, rowEdit, changes, academicQual]
  );

  const handleCancelChanges = () => {
    const rolledBackReviews = originalData.map((obj) => ({ ...obj }));
    setAcademicQual(rolledBackReviews);
    setDeletedItems([]);
    setChanges({});
    showMessage({
      message: t("common:changesCancelled"),
      type: "info",
      duration: 3000,
    });
  };

  // * Validator 
  // TODO Can be abstracted as well probably -- But column names matter ? 
  const validateInput = () => {
    let ret = [true, t('common:validationSuccess')]
    academicQual.forEach((itr) => {
      // * Check empty description
      if (itr.status === '')
        ret = [false, t('common:statusEmpty')]
      else if (itr.result === '')
        ret = [false, t('common:resEmpty')]
      //else if (itr.institution === '')
      //ret = [false, t('common:institutionEmpty')]
      else if (itr.qualCode === '')
        ret = [false, t('common:titleAwarededEmpty')]
    })
    return (ret)
  }

  const handleSave = async () => {
    try {
      if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
        showMessage({
          message: t('common:noChanges'),
          type: "danger",
          duration: 3000,
        })
        return;
      }
      let valRet = validateInput();
      if (!valRet[0]) {
        showMessage({
          message: valRet[1],
          type: "danger",
          duration: 3000,
        })
        return;
      }

      let savePromises = [];
      let updateSucc = true;
      let insertSucc = true;

      savePromises.push(
        saveAcademicQual(
          changes,
          props?.selectedEmployee?.employeeID
        ).then((resp) => {
          updateSucc = true;
        }).catch((error) => {
          console.error("Save error", error);
          updateSucc = false;
        })
      );
      if (deletedItems.length > 0) {
        savePromises.push(deleteAcademicQual(deletedItems)
          .then((res) => {
            if (res) {
              deleteSucc = true
              removeFromChangesAfterSave();
            }
          }).catch((e) => { console.error('failed to delete', e) }))
      }
      // Wait for all promises to resolve
      await Promise.all(savePromises);
      // === Handle results ===
      if (!updateSucc || !insertSucc) {
        showMessage({
          message: t("common:errorSave"),
          type: "danger",
          duration: 3000,
        });
        return;
      }
      // * Reload after save
      loadData();
      removeFromChangesAfterSave();
      // === Success message ===
      showMessage({
        message: t("common:academicQualSuccessSave"),
        type: "success",
        duration: 3000,
      });

    } catch (error) {
      showMessage({
        message: t("common:errorSave"),
        type: "danger",
        duration: 3000,
      });
    }
  };

  const handleDelete = (item, index, attribute) => {
    rowDelete(item, index, attribute, changes, setChanges, academicQual, setAcademicQual, setDeletedItems, ref)
  }

  const removeFromChangesAfterSave = () => {
    setDeletedItems([]);
    setChanges({});
  };

  const renderAcademicQualifications = useCallback(
    ({ item, index }) => (
      <Card style={[common.cardstyle]}>
        <Card.Content>
          <AcademicQualificationsListView
            item={item}
            index={index}
            handleEditPress={handleEditPress}
            academicQualHelper={academicQualHelper}
            styles={styles}
            t={t}
            handleDelete={handleDelete}
          />
        </Card.Content>
      </Card>
    ),
    [
      academicQual,
      handleEditPress,
      handleDelete,
      styles,
    ]
  );

  if (isLoading) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator animating={true} color={colors.blue} />
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.mainView}>
      <View style={styles.container}>
        <View style={[styles.buttons, styles.functionalityButtons]}>
          <CustomButton
            text={t("common:new")}
            onPress={() => {
              rowAdd(
                academicQual,
                setAcademicQual,
                ['rowGuid', 'employeeId'],
                [],
                ref,
                t,
                ['employeeId'],
                ['years'],
                changes,
                setChanges,
                undefined,
                undefined,
                "rowGuid",
                false,
                ['dos', 'doq']
              );
            }}
            type="BUTTON_ADD"
            textTop="0"
            sz={15}
          />
          {(Object.keys(changes).length > 0 ||
            changes.length > 0 || deletedItems.length > 0) && (
              <CustomButton
                text={t("common:saveFunction")}
                onPress={handleSave}
                type="BUTTON_SAVE"
                isGradient={true}
                textTop="0"
                sz={15}
              />
            )}
          <CustomButton
            text={t("common:cancel")}
            onPress={handleCancelChanges}
            type="BUTTON_CANCEL"
            textTop="0"
            sz={15}
          />
        </View>
        <FlatList
          data={academicQual}
          keyExtractor={(item, index) => `${item.rowGuid}-${index}`}
          renderItem={renderAcademicQualifications} // Use the memoized function here
          refreshing={isLoading}
          ref={ref}
          ListHeaderComponent={
            <SafeAreaView style={common.listTitle}>
              <SafeAreaView style={styles.row}>
                <CustomLabel value={t("common:institution")} type="SUBTITLE" />
                <CustomLabel value={t("common:titleAwareded")} type="SUBTITLE" />
                <CustomLabel value={t("common:fos")} type="SUBTITLE" />
                <CustomLabel value={t("common:res")} type="SUBTITLE" />
                <CustomLabel value={t("common:Status")} type="SUBTITLE" />
                <SafeAreaView style={[{ marginRight: '5%' }]} />
              </SafeAreaView>
              <SafeAreaView style={common.divider} />
            </SafeAreaView>
          }
          ListEmptyComponent={
            isLoading ? (
              <ActivityIndicator animating={true} color={colors.blue} />
            ) : (
              <CustomLabel
                value={t("common:AcadNoReviewDataPreview")}
                type="NO_FUNCTIONS"
              />
            )
          }
          initialNumToRender={4}
        />
      </View>
    </SafeAreaView>
  );
};

const makeStyles = (colors, width) =>
  StyleSheet.create({
    mainView: {
      flex: 1,
    },
    rowView: {
      flex: 1,
      flexDirection: "row",
      justifyContent: "center",
    },
    container: {
      flex: 1,
    },
    cardStyleBranch: {
      margin: 5,
      marginHorizontal: 10,
      borderRadius: 5,
      elevation: 5,
      shadowColor: colors.card_line,
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0.3,
      shadowRadius: 5,
      backgroundColor: colors.background_card,
    },
    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
    },
    row2: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      marginHorizontal: 10,
      marginBottom: 50
    },
    item: {
      flex: 1,
      justifyContent: "center",
      marginRight: 6
    },
    itemCurrency: {
      flex: 1,
      justifyContent: "center",
      marginRight: 6,
      flexDirection: "column",
      gap: 10
    },
    buttons: {
      flexDirection: "row",
      justifyContent: "flex-end",
      alignSelf: "flex-end",
      marginRight: 15,
    },
    functionalityButtons: {
      justifyContent: "flex-end",
    },
    loader: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "4%",
    },
    icons: {
      fontSize: 24,
      marginHorizontal: 10,
    },
    backButton: {
      flexDirection: "row",
      justifyContent: "flex-end",
    },
  });

export default HRAdminAcademicQualifications;
